/**
 * ===========================================================================
 *  engine.js — the audio engine proper
 * ===========================================================================
 * Everything that is not "talking to the game" lives here: the mixer, the
 * voice budget, the throttles, the trigger log, and the one `play()` that all
 * of it funnels through.
 *
 * It is deliberately built on a bare `BaseAudioContext`, so the SAME code path
 * runs against the live `AudioContext` in the browser and against an
 * `OfflineAudioContext` in `analysis.js`. The waveform statistics reported for
 * a sword impact are therefore measurements of the graph that actually plays,
 * not of a parallel "test" implementation.
 * ===========================================================================
 */
import { RNG } from '../core/rng.js';
import { createGraph, createListener, place, place2D, MAX_DIST, distanceGain } from './graph.js';
import { SFX, resolve } from './sfx.js';
import { voiceShot, voicePitch, GESTURE } from './voice.js';
import { createMusic } from './music.js';
import { createAmbience } from './ambience.js';

/** id -> {bus, build|voice, gap, len} for every playable name. */
export const DEFS = (() => {
  const m = Object.create(null);
  for (const id of Object.keys(SFX)) {
    const d = resolve(id);
    if (d) m[id] = { id, bus: d.bus, build: d.build, gap: d.gap ?? 0.03, len: d.len ?? 0.5, prio: 1 };
  }
  for (const g of Object.keys(GESTURE)) {
    m['voice_' + g] = { id: 'voice_' + g, bus: 'voice', voice: g, gap: 0.05, len: GESTURE[g].dur + 0.4, prio: 1 };
  }
  // priorities: these must never be dropped by the voice budget
  for (const id of ['impact_musou', 'musou_start', 'musou_charge', 'ko_chime', 'ko', 'guardBreak',
    'guard_break', 'gate_open', 'ui_click', 'ui_confirm', 'ui_back']) if (m[id]) m[id].prio = 3;
  for (const id of ['impact_slam', 'guard', 'clang', 'swing_huge', 'launcher', 'voice_shout',
    'voice_death']) if (m[id]) m[id].prio = 2;
  return m;
})();

export const PLAYABLE = Object.keys(DEFS).sort();

const LOG_N = 320;
const VOICE_CAP = 44;               // simultaneous scheduled sounds
const SCHED_LATENCY = 0.012;        // never schedule in the past

export function createEngine(ctx, opts = {}) {
  const rng = new RNG(opts.seed ?? 0x5A17D0);
  const rnd = () => rng.next();
  const graph = createGraph(ctx, rnd, opts.mix || {});
  const listener = createListener();

  /* Reused scratch — play() must not allocate per call beyond the audio nodes
   * themselves. `A` is the recipe context, `_p` the parameter bag, `_out` the
   * placement result. */
  const A = { ctx, g: graph, out: null, rnd, n: graph.noise, _formants: [null, null, null] };
  const _p = { gain: 1, rate: 1, type: 'effort', f0: 120, power: 1 };
  const _out = { gain: 0, pan: 0, dist: 0, delay: 0 };

  const lastAt = Object.create(null);          // id -> ctx time of last trigger
  const ends = new Float64Array(192);
  let ei = 0;

  /* ------------------------------------------------------------- the log */
  const ring = new Array(LOG_N);
  for (let i = 0; i < LOG_N; i++) ring[i] = { t: 0, id: '', bus: '', gain: 0, pan: 0, dist: 0, at: 0, n: 0 };
  let ri = 0, total = 0;
  const counts = Object.create(null);
  let clock = () => ctx.currentTime;

  function log(id, bus, gain, pan, dist) {
    const r = ring[ri];
    r.t = +clock().toFixed(4); r.id = id; r.bus = bus;
    r.gain = +gain.toFixed(4); r.pan = +pan.toFixed(3); r.dist = +dist.toFixed(2);
    r.at = +ctx.currentTime.toFixed(4); r.n = total;
    ri = (ri + 1) % LOG_N; total++;
    counts[id] = (counts[id] || 0) + 1;
    return r;
  }

  function activeVoices(now) {
    let n = 0;
    for (let i = 0; i < ends.length; i++) if (ends[i] > now) n++;
    return n;
  }

  /* ------------------------------------------------------------- play --- */
  /**
   * opts: { x,y,z, gain, rate, variation, delay, pan, verb, seed, kind, dry }
   * Returns a handle {id, at, stop(when)} or null if it was throttled/dropped.
   */
  function play(id, o) {
    const def = DEFS[id];
    const now = ctx.currentTime;
    if (!def) { log(id + '?', 'none', 0, 0, 0); return null; }

    const gainIn = o && o.gain !== undefined ? o.gain : 1;
    const positional = o && (o.x !== undefined || o.z !== undefined);

    // --- retrigger throttle: a 30-body sweep is ONE clang, not thirty.
    //     `opts.force` skips it, for scripted one-offs and probes.
    const last = lastAt[id] || -1e9;
    if (!(o && o.force) && now - last < def.gap) return null;

    // --- voice budget
    const act = activeVoices(now);
    if (act >= VOICE_CAP && def.prio < 3) {
      if (def.prio < 2 || act >= VOICE_CAP + 12) return null;
    }

    const bus = graph.buses[def.bus] || graph.buses.sfx;
    let node = null;
    if (positional) {
      node = place(graph, bus, o.x || 0, o.y || 0, o.z || 0, listener, gainIn, _out, o);
      if (!node) return null;                       // out of earshot
    } else {
      node = place2D(graph, bus, gainIn * (o?.close ? 1 : 0.9), (o && o.pan) || 0, _out);
    }

    const t = now + SCHED_LATENCY + (_out.delay || 0) + ((o && o.delay) || 0);
    lastAt[id] = now;

    A.out = node;
    /* `_p.gain` is TIMBRE, not level: it says how hard the blow was, so a big
     * sweep gets a fatter body and a longer ring. The placement node above
     * already carries the actual level (caller gain x distance). */
    _p.gain = (o && o.power !== undefined)
      ? Math.max(0.1, Math.min(2.0, o.power))
      : Math.max(0.35, Math.min(1.6, 0.55 + 0.45 * gainIn));
    _p.rate = (o && o.rate) || 1;
    if (def.voice) {
      _p.type = def.voice;
      _p.f0 = (o && o.f0) || voicePitch((o && o.seed) || 0, (o && o.kind) || 1);
      _p.gain = 1;
      voiceShot(A, t, _p);
    } else {
      def.build(A, t, _p);
    }
    ends[ei] = t + def.len; ei = (ei + 1) % ends.length;
    log(id, def.bus, _out.gain, _out.pan, _out.dist);
    return { id, at: t, node, stop(when) { try { node.gain?.setTargetAtTime(0.0001, when ?? ctx.currentTime, 0.05); } catch { /* not a gain */ } } };
  }

  /** 2D helper for ambience/UI that never needs a world position. */
  function play2(id, gain, pan, verb) {
    return play(id, { gain, pan, verb, close: true });
  }

  const music = createMusic(graph, rnd);
  const ambience = createAmbience(graph, rnd, play2);

  return {
    ctx, graph, listener, music, ambience, rng,
    play, play2,
    setClock(fn) { clock = fn; },
    setBus(name, v) { graph.setBus(name, v); },
    busGain(name) { return graph.busGain(name); },
    get log() {
      const out = [];
      for (let i = 0; i < LOG_N; i++) {
        const r = ring[(ri + i) % LOG_N];
        if (r.id) out.push({ t: r.t, id: r.id, bus: r.bus, gain: r.gain, pan: r.pan, dist: r.dist, at: r.at, n: r.n });
      }
      return out;
    },
    counts() { return { ...counts }; },
    clearLog() { for (const r of ring) { r.id = ''; } ri = 0; total = 0; for (const k of Object.keys(counts)) delete counts[k]; },
    get total() { return total; },
    activeVoices,
    stats() {
      return {
        total, voices: activeVoices(ctx.currentTime),
        buses: { master: +graph.master.gain.value.toFixed(3), music: +graph.busGain('music').toFixed(3),
          sfx: +graph.busGain('sfx').toFixed(3), ambience: +graph.busGain('ambience').toFixed(3),
          voice: +graph.busGain('voice').toFixed(3) },
        music: music.stats(), ambience: ambience.stats(),
        listener: { x: +listener.x.toFixed(2), y: +listener.y.toFixed(2), z: +listener.z.toFixed(2) },
      };
    },
    dispose() { try { music.stop(0.05); ambience.stop(0.05); graph.dispose(); } catch { /* context gone */ } },
  };
}

export { MAX_DIST, distanceGain };
